// ---------------------------------------------------------------------------
// Who owns the socket.
//
// It used to be `RoomPage`, and that is why joining by code showed two screens
// for one action. The home screen looked a room up, navigated, and the room page
// then started from nothing — a second lookup, a second bot-protection
// challenge, and a socket connect — behind a page of its own that said "Getting
// you a seat". A player who typed four characters watched a spinner, a page
// change, another spinner, and another page change.
//
// So the connection is opened ABOVE the router and outlives the navigation. The
// home screen opens it, waits on its own Join button, and navigates only when
// there is a room to navigate into; the room page finds it already running and
// draws the lobby on its first frame. Nothing in between renders at all.
//
// Three things about the shape here are load-bearing:
//
//   1. **Only what identifies the socket is asked for.** A caller names the
//      room, the Turnstile token it cleared and whether it wants a spectator
//      seat, and nothing else: those three are what make `useGameConnection`
//      tear a socket down and build a new one. The account half is read here,
//      from the context directly above, so no caller can restart a live socket
//      by handing over a token function its own provider re-created.
//   2. **A second `join` for the room already connected is ignored.** That is
//      the whole handoff: the home screen's token is what the server was given,
//      and the room page arriving with no token of its own must not replace it.
//      The room page does not need one — the seat is already taken and its
//      reconnect token is in the player store.
//   3. **The connection follows the URL out of the room.** Leaving to anywhere
//      that is not the room's own path drops it, so a socket cannot outlive the
//      page that wanted it. `/` is the one exception, and only until the room
//      has been entered once: that is the home screen holding a join in flight.
//
// There is deliberately no second socket anywhere. Two would mean the server
// seeing a disconnect and a rejoin for every player who joins by code, which is
// a "somebody dropped out" event in front of the whole room, every time.
// ---------------------------------------------------------------------------

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAccount } from './useAccount.js';
import { useGameConnection, type GameConnection, type JoinOptions } from './useGameConnection.js';

/**
 * What a caller has to decide, and the whole of what identifies the socket.
 * Anything else on a join — the session token, whether anybody is signed in —
 * is read from the account context here.
 */
export interface RoomTarget {
  roomCode: string;
  turnstileToken?: string;
  asSpectator?: boolean;
}

export interface RoomConnectionApi {
  connection: GameConnection;
  /** The room a socket is open for, or null. */
  joiningRoom: string | null;
  /**
   * Open a connection for this room. Asking for the room that is already
   * connected does nothing at all, which is the home-screen-to-room-page
   * handoff: the token the server was given must not be replaced with the room
   * page's own absence of one.
   */
  join: (target: RoomTarget) => void;
  /** Close it. Called by whoever gave up, not by whoever moved on. */
  leave: () => void;
}

const RoomConnectionContext = createContext<RoomConnectionApi | null>(null);

export function RoomConnectionProvider({ children }: { children: ReactNode }) {
  const location = useLocation();

  const [target, setTarget] = useState<RoomTarget | null>(null);

  // Read here rather than passed in. `freshToken` is called at the moment a
  // join is sent — a Clerk session JWT lives about a minute, so anything
  // settled earlier is stale — and `signedIn` flipping is what re-sends the
  // join on a sign-in that lands with the socket already open, so the server
  // can upgrade the seat in place.
  const account = useAccount();

  const join = useCallback((wanted: RoomTarget) => {
    setTarget((current) =>
      // The identical object back, so React bails out rather than re-rendering.
      // A join for the room already connected changes nothing at all — see the
      // handoff note at the top of this file.
      current !== null && current.roomCode === wanted.roomCode
        ? current
        : {
            roomCode: wanted.roomCode,
            turnstileToken: wanted.turnstileToken,
            asSpectator: wanted.asSpectator,
          },
    );
  }, []);

  const leave = useCallback(() => setTarget(null), []);

  const options = useMemo<JoinOptions | null>(
    () =>
      target === null
        ? null
        : { ...target, getClerkToken: account.freshToken, signedIn: account.signedIn },
    [target, account.freshToken, account.signedIn],
  );

  const connection = useGameConnection(options);

  // The socket follows the URL out of the room. `entered` is what tells a join
  // still in flight on the home screen apart from a player who has walked back
  // out to it — the first must survive being at `/`, the second must not.
  const entered = useRef(false);
  useEffect(() => {
    if (target === null) {
      entered.current = false;
      return;
    }
    const here = location.pathname.toUpperCase();
    if (here === `/ROOM/${target.roomCode.toUpperCase()}`) {
      entered.current = true;
      return;
    }
    if (here === '/' && !entered.current) return;
    setTarget(null);
  }, [target, location.pathname]);

  const value = useMemo<RoomConnectionApi>(
    () => ({ connection, joiningRoom: target?.roomCode ?? null, join, leave }),
    [connection, target?.roomCode, join, leave],
  );

  return <RoomConnectionContext.Provider value={value}>{children}</RoomConnectionContext.Provider>;
}

/**
 * The one connection. Throws outside the provider rather than opening a second
 * socket quietly: two sockets is the failure this file exists to prevent, and it
 * would show up as players being displaced from their own seats.
 */
export function useRoomConnection(): RoomConnectionApi {
  const api = useContext(RoomConnectionContext);
  if (api === null) {
    throw new Error('useRoomConnection needs a <RoomConnectionProvider> above it.');
  }
  return api;
}
